import {
  Component,
  DestroyRef,
  effect,
  computed,
  inject,
  input,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { Address } from '@awo-care/shared-core';
import {
  GoogleMapsAutoCompleteComponent,
  PhoneInputComponent,
} from '@solidgroup-agency/ui-dynamic-form';
import {
  adoptGoogleMatch,
  applyManualEdit,
  buildManualAddress,
  extractEditFields,
  hasAddressFieldsChanged,
} from './single-parent-form.utils';
import { GeoPoint } from 'firebase/firestore';

@Component({
  selector: 'lib-single-parent-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    GoogleMapsAutoCompleteComponent,
    PhoneInputComponent,
  ],
  template: `
    <div class="parent-form" [formGroup]="parentForm()">
      <mat-form-field appearance="outline">
        <mat-label i18n="@@lib.single-parent-form.first-name">Vorname</mat-label>
        <input
          matInput
          formControlName="firstName"
          required
          i18n-placeholder="@@lib.single-parent-form.first-name-placeholder"
          placeholder="Vorname eingeben"
          [attr.data-testid]="parentType() + '-firstname'"
        />
        @if (parentForm().get('firstName')?.hasError('required') &&
        parentForm().get('firstName')?.touched) {
        <mat-error i18n="@@lib.single-parent-form.first-name-required">Vorname ist erforderlich</mat-error>
        }
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label i18n="@@lib.single-parent-form.last-name">Nachname</mat-label>
        <input
          matInput
          formControlName="lastName"
          required
          i18n-placeholder="@@lib.single-parent-form.last-name-placeholder"
          placeholder="Nachname eingeben"
          [attr.data-testid]="parentType() + '-lastname'"
        />
        @if (parentForm().get('lastName')?.hasError('required') &&
        parentForm().get('lastName')?.touched) {
        <mat-error i18n="@@lib.single-parent-form.last-name-required">Nachname ist erforderlich</mat-error>
        }
      </mat-form-field>

      <lib-phone-input
        [label]="phoneLabel"
        formControlName="phoneMobile"
        [placeholder]="phonePlaceholder"
        [required]="true"
        [errorRequired]="phoneErrorRequired"
        [errorPattern]="phoneErrorPattern"
        [attr.data-testid]="parentType() + '-phone'"
        [attr.id]="parentType() + '-phone-input'"
        [attr.name]="parentType() + '-phoneMobile'"
      />

      @if (parentType() === 'parent2') {
      <mat-form-field appearance="outline">
        <mat-label i18n="@@common.email">E-Mail</mat-label>
        <input
          matInput
          type="email"
          autocomplete="email"
          formControlName="email"
          i18n-placeholder="@@lib.single-parent-form.email-placeholder"
          placeholder="E-Mail-Adresse eingeben"
          [attr.data-testid]="parentType() + '-email'"
        />
        @if (parentForm().get('email')?.hasError('email') &&
        parentForm().get('email')?.touched) {
        <mat-error i18n="@@common.validation.emailInvalid">Bitte geben Sie eine gültige E-Mail-Adresse ein</mat-error>
        }
      </mat-form-field>
      }

      @if(parentType() === 'parent1') { @if (useGoogleMapsAutocomplete()) {
        @if (showAutocomplete()) {
          <lib-google-maps-autocomplete
            formControlName="address"
            [initialAddress]="$safeNavigationMigration(parentForm().get('address')?.value)"
            [required]="true"
            [restrictToCountry]="foreignAddress() ? undefined : 'de'"
            [searchTypes]="['street_address', 'route', 'premise']"
            [label]="addressLabel"
            [placeholder]="addressPlaceholder"
            [errorRequired]="addressErrorRequired"
            [errorMissingHouseNumber]="addressErrorMissingHouseNumber"
            [apiLoadErrorMessage]="addressApiLoadError"
            [locationNotFoundMessage]="addressLocationNotFound"
            [incompleteAddressMessage]="addressIncomplete"
            (addressChange)="onAddressChange($event)"
            [attr.data-testid]="parentType() + '-address'"
          />
          @if (!isDisabled()) {
            <button
              type="button"
              class="link-button enter-manually"
              (click)="onEnterAddressManually()"
              [attr.data-testid]="parentType() + '-enter-manually'"
              i18n="@@lib.single-parent-form.enter-manually"
            >
              Adresse nicht dabei? Manuell eintragen
            </button>
          }
        }

        @if (showEditableFields()) {
          <div class="manual-address-fields" [formGroup]="manualEditForm" [attr.data-testid]="parentType() + '-manual-address'">
            <div class="manual-address-row street-row">
              <mat-form-field appearance="outline" class="street">
                <mat-label i18n="@@lib.single-parent-form.street">Straße</mat-label>
                <input
                  matInput
                  formControlName="street"
                  required
                  i18n-placeholder="@@lib.single-parent-form.street-placeholder"
                  placeholder="z.B. Musterstraße"
                  [attr.data-testid]="parentType() + '-street'"
                />
                @if (manualEditForm.get('street')?.hasError('required') && manualEditForm.get('street')?.touched) {
                  <mat-error i18n="@@lib.single-parent-form.street-required">Pflichtfeld</mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline" class="house-number">
                <mat-label i18n="@@lib.single-parent-form.house-number">Hausnr.</mat-label>
                <input
                  matInput
                  formControlName="houseNumber"
                  required
                  i18n-placeholder="@@lib.single-parent-form.house-number-placeholder"
                  placeholder="Nr."
                  [attr.data-testid]="parentType() + '-house-number'"
                />
                @if (manualEditForm.get('houseNumber')?.hasError('required') && manualEditForm.get('houseNumber')?.touched) {
                  <mat-error i18n="@@lib.single-parent-form.house-number-required">Pflichtfeld</mat-error>
                }
              </mat-form-field>
            </div>

            <div class="manual-address-row">
              <mat-form-field appearance="outline" class="zip">
                <mat-label i18n="@@lib.single-parent-form.zip">PLZ</mat-label>
                <input
                  matInput
                  formControlName="zipCode"
                  required
                  inputmode="numeric"
                  maxlength="5"
                  i18n-placeholder="@@lib.single-parent-form.zip-placeholder"
                  placeholder="PLZ"
                  [attr.data-testid]="parentType() + '-zip'"
                />
                @if (manualEditForm.get('zipCode')?.hasError('required') && manualEditForm.get('zipCode')?.touched) {
                  <mat-error i18n="@@lib.single-parent-form.zip-required">Pflichtfeld</mat-error>
                } @else if (manualEditForm.get('zipCode')?.hasError('pattern') && manualEditForm.get('zipCode')?.touched) {
                  <mat-error i18n="@@lib.single-parent-form.zip-pattern">5 Ziffern erforderlich</mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline" class="city">
                <mat-label i18n="@@lib.single-parent-form.city">Ort</mat-label>
                <input
                  matInput
                  formControlName="city"
                  required
                  i18n-placeholder="@@lib.single-parent-form.city-placeholder"
                  placeholder="Ort"
                  [attr.data-testid]="parentType() + '-city'"
                />
                @if (manualEditForm.get('city')?.hasError('required') && manualEditForm.get('city')?.touched) {
                  <mat-error i18n="@@lib.single-parent-form.city-required">Pflichtfeld</mat-error>
                }
              </mat-form-field>
            </div>

            @if (addressNotFound()) {
              <p class="manual-address-hint" i18n="@@lib.single-parent-form.address-manual-hint">
                Ihre Adresse wurde nicht in der Karte gefunden. Wir übernehmen Ihre Eingabe und prüfen sie manuell.
              </p>
              @if (!isDisabled()) {
                <button
                  type="button"
                  class="link-button"
                  (click)="onSearchAgain()"
                  [attr.data-testid]="parentType() + '-back-to-autocomplete'"
                  i18n="@@lib.single-parent-form.back-to-autocomplete"
                >
                  Zurück zur Adresssuche
                </button>
              }
            } @else if (!isDisabled()) {
              <button
                type="button"
                class="link-button"
                (click)="onSearchAgain()"
                [attr.data-testid]="parentType() + '-search-again'"
                i18n="@@lib.single-parent-form.search-again"
              >
                Andere Adresse suchen
              </button>
            }

            @if (parentForm().get('address')?.errors?.['missingHouseNumber']) {
              <p
                class="address-stale-error"
                [attr.data-testid]="parentType() + '-address-stale-error'"
                i18n="@@lib.single-parent-form.address-stale-error"
              >
                Adresse muss neu ausgewählt werden — bitte oben „Andere Adresse suchen" anklicken.
              </p>
            }
          </div>
        }

        <div class="foreign-address-toggle">
          <span class="foreign-address-label" i18n="@@lib.single-parent-form.foreign-address">Adresse außerhalb von Deutschland</span>
          <mat-slide-toggle
            [checked]="foreignAddress()"
            [disabled]="isDisabled() || addressNotFound()"
            (change)="onForeignAddressToggle($event.checked)"
            [attr.data-testid]="parentType() + '-foreign-address-toggle'"
          />
        </div>
      } @else {
      <mat-form-field appearance="outline">
        <mat-label i18n="@@lib.single-parent-form.address">Adresse</mat-label>
        <input
          matInput
          [value]="addressDisplayValue()"
          i18n-placeholder="@@lib.single-parent-form.address-placeholder"
          placeholder="Adresse eingeben"
          readonly
        />
        @if (parentForm().get('address')?.hasError('required') &&
        parentForm().get('address')?.touched) {
        <mat-error i18n="@@lib.single-parent-form.address-required">Adresse ist erforderlich</mat-error>
        }
      </mat-form-field>
      } } @if(parentType() === 'parent2') {
      <mat-form-field appearance="outline">
        <mat-label i18n="@@lib.single-parent-form.alternative-address">Evtl. abweichende Adresse zum Kind</mat-label>
        <input
          matInput
          formControlName="alternativeAddress"
          i18n-placeholder="@@lib.single-parent-form.alternative-address-placeholder"
          placeholder="Alternative Adresse eingeben"
        />
      </mat-form-field>
      }

      <div class="radio-group" [attr.data-testid]="parentType() + '-employed'">
        <div class="radio-label" i18n="@@lib.single-parent-form.employed">Berufstätig</div>
        <mat-radio-group formControlName="employed" class="radio-buttons">
          <mat-radio-button [value]="true" i18n="@@common.yes">Ja</mat-radio-button>
          <mat-radio-button [value]="false" i18n="@@common.no">Nein</mat-radio-button>
        </mat-radio-group>
        @if (parentForm().get('employed')?.hasError('required') &&
        parentForm().get('employed')?.touched) {
        <mat-error i18n="@@lib.single-parent-form.employed-required">Bitte wählen Sie eine Option aus</mat-error>
        }
      </div>

      @if(parentForm().get('employed')?.value) {
      <mat-form-field appearance="outline">
        <mat-label i18n="@@lib.single-parent-form.occupation">Beruf</mat-label>
        <input
          matInput
          formControlName="occupation"
          type="text"
          i18n-placeholder="@@lib.single-parent-form.occupation-placeholder"
          placeholder="Beruf eingeben"
          [attr.data-testid]="parentType() + '-occupation'"
        />
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label i18n="@@lib.single-parent-form.weekly-work-hours">Wöchentliche Arbeitszeit</mat-label>
        <input
          matInput
          formControlName="weeklyWorkHours"
          type="text"
          pattern="[0-9]+([,.][0-9])?"
          i18n-placeholder="@@lib.single-parent-form.weekly-work-hours-placeholder"
          placeholder="0-40h (z.B. 38,5)"
          (input)="onDecimalInput($event)"
          [attr.data-testid]="parentType() + '-workhours'"
        />
        @if (parentForm().get('weeklyWorkHours')?.errors?.['required']) {
        <mat-error i18n="@@lib.single-parent-form.weekly-work-hours-required">Pflichtfeld</mat-error>
        } @if (parentForm().get('weeklyWorkHours')?.errors?.['min']) {
        <mat-error i18n="@@lib.single-parent-form.weekly-work-hours-min">Minimum 0 Stunden</mat-error>
        } @if (parentForm().get('weeklyWorkHours')?.errors?.['max']) {
        <mat-error i18n="@@lib.single-parent-form.weekly-work-hours-max">Maximum 40 Stunden</mat-error>
        } @if (parentForm().get('weeklyWorkHours')?.errors?.['invalidDecimal'])
        {
        <mat-error i18n="@@lib.single-parent-form.weekly-work-hours-invalid-decimal">Nur eine Dezimalstelle erlaubt (z.B. 38,5)</mat-error>
        }
      </mat-form-field>

      <div class="info-note">
        <p i18n="@@lib.single-parent-form.employment-verification-note">
          Sollten nicht ausreichend Betreuungsplätze zur Verfügung stehen,
          werden die hier gemachten Angaben mittels einer
          Arbeitgeberbescheinigung überprüft.
        </p>
      </div>
      }

      <div
        class="radio-group"
        [attr.data-testid]="parentType() + '-legalguardian'"
      >
        <div class="radio-label" i18n="@@lib.single-parent-form.legal-guardian">Erziehungsberechtigt</div>
        <mat-radio-group formControlName="legalGuardian" class="radio-buttons">
          <mat-radio-button [value]="true" i18n="@@common.yes">Ja</mat-radio-button>
          <mat-radio-button [value]="false" i18n="@@common.no">Nein</mat-radio-button>
        </mat-radio-group>
        @if (parentForm().get('legalGuardian')?.hasError('required') &&
        parentForm().get('legalGuardian')?.touched) {
        <mat-error i18n="@@lib.single-parent-form.legal-guardian-required">Bitte wählen Sie eine Option aus</mat-error>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .parent-form {
        display: grid;
        gap: 1rem;
        margin-top: 1.5rem;
      }

      .radio-group {
        display: grid;
        gap: 0.5rem;
      }

      .radio-label {
        font-size: 1rem;
        color: rgba(0, 0, 0, 0.87);
      }

      .radio-buttons {
        display: flex;
        gap: 2rem;
      }

      .info-note {
        color: #666;
        font-size: 0.875rem;
        line-height: 1.5;
        margin-top: -1rem;
        margin-bottom: 1rem;
        background-color: #f5f5f5;
        border-radius: 4px;

        p {
          margin: 0;
          padding: 1rem;
        }
      }

      .error-message {
        color: #f44336;
        font-size: 0.75rem;
        margin-top: -0.5rem;
        margin-bottom: 1rem;
      }

      .foreign-address-toggle {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 0.5rem;
        margin-top: -0.5rem;
      }

      .foreign-address-label {
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.54);
      }

      .manual-address-fields {
        display: grid;
        gap: 0.5rem;
      }

      .manual-address-row {
        display: grid;
        grid-template-columns: minmax(6rem, 1fr) 2fr;
        gap: 0.5rem;
      }

      .manual-address-row.street-row {
        grid-template-columns: 3fr minmax(5rem, 1fr);
      }

      @media (max-width: 600px) {
        .manual-address-row,
        .manual-address-row.street-row {
          grid-template-columns: 1fr;
        }

        .foreign-address-toggle {
          justify-content: flex-start;
        }

        .link-button.enter-manually {
          align-self: flex-start;
        }
      }

      .manual-address-hint {
        margin: 0;
        font-size: 0.8125rem;
        color: rgba(0, 0, 0, 0.6);
        line-height: 1.4;
      }

      .address-stale-error {
        margin: 0.5rem 0 0;
        font-size: 0.8125rem;
        color: var(--mat-sys-error, #d32f2f);
        line-height: 1.4;
      }

      .link-button {
        align-self: flex-start;
        background: none;
        border: none;
        padding: 0;
        color: var(--theme-accent, #1976d2);
        font-size: 0.8125rem;
        text-decoration: underline;
        cursor: pointer;
      }

      .link-button:hover {
        text-decoration: none;
      }

      .link-button.enter-manually {
        margin-top: -0.5rem;
        align-self: flex-end;
      }

      mat-form-field {
        width: 100%;
      }
    `,
  ],
})
export class SingleParentFormComponent {
  parentForm = input.required<FormGroup>();
  parentType = input.required<'parent1' | 'parent2'>();
  useGoogleMapsAutocomplete = input<boolean>(false);
  foreignAddress = signal(false);
  addressNotFound = signal(false);
  readOnly = input<boolean>(false);
  editMode = input<boolean>(false);

  private readonly fb = new FormBuilder();
  private readonly destroyRef = inject(DestroyRef);
  readonly manualEditForm = this.fb.group({
    street: ['', [Validators.required]],
    houseNumber: ['', [Validators.required]],
    zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
    city: ['', [Validators.required]],
  });

  readonly addressValue = signal<Address | null>(null);
  private subscribedAddressControl = false;

  // Phone input translations
  readonly phoneLabel = $localize`:@@lib.single-parent-form.phone:Telefon`;
  readonly phonePlaceholder = $localize`:@@lib.single-parent-form.phone-placeholder:Telefonnummer eingeben`;
  readonly phoneErrorRequired = $localize`:@@lib.single-parent-form.phone-required:Telefonnummer ist erforderlich`;
  readonly phoneErrorPattern = $localize`:@@lib.single-parent-form.phone-invalid:Ungültige Telefonnummer`;

  // Google Maps address input translations
  readonly addressLabel = $localize`:@@lib.single-parent-form.address:Adresse`;
  readonly addressPlaceholder = $localize`:@@lib.single-parent-form.address-placeholder:Adresse eingeben`;
  readonly addressErrorRequired = $localize`:@@lib.single-parent-form.address-required:Adresse ist erforderlich`;
  readonly addressErrorMissingHouseNumber = $localize`:@@lib.single-parent-form.address-missing-house-number:Hausnummer fehlt`;
  readonly addressApiLoadError = $localize`:@@lib.single-parent-form.address-api-error:Adresssuche konnte nicht geladen werden`;
  readonly addressLocationNotFound = $localize`:@@lib.single-parent-form.address-not-found:Adresse nicht gefunden`;
  readonly addressIncomplete = $localize`:@@lib.single-parent-form.address-incomplete:Adresse unvollständig`;

  constructor() {
    effect(() => {
      const form = this.parentForm();
      const addressControl = form.get('address');
      if (!addressControl || this.subscribedAddressControl) {
        return;
      }
      this.subscribedAddressControl = true;

      const initial = addressControl.value as Address | null | undefined;
      this.addressValue.set(initial ?? null);
      this.handleAddressChange(initial);

      addressControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value: Address | null | undefined) => {
          this.addressValue.set(value ?? null);
          this.handleAddressChange(value);
        });
    });

    this.manualEditForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.propagateManualEdit();
      });

    effect(() => {
      const disabled = this.isDisabled();
      const addressControl = this.parentForm().get('address');
      if (disabled) {
        addressControl?.disable({ emitEvent: false });
        this.manualEditForm.disable({ emitEvent: false });
      } else {
        addressControl?.enable({ emitEvent: false });
        this.manualEditForm.enable({ emitEvent: false });
      }
    });
  }

  private handleAddressChange(address: Address | null | undefined): void {
    this.syncManualEditFormFromAddress(address);
    if (address?.addressManuallyEntered && !address.placeId) {
      this.addressNotFound.set(true);
    }
  }

  isDisabled = computed(() => {
    return this.readOnly() && !this.editMode();
  });

  hasSelectedAddress = computed(() => {
    const address = this.addressValue();
    return Boolean(address?.street || address?.city || address?.zipCode);
  });

  showEditableFields = computed(() => this.addressNotFound() || this.hasSelectedAddress());

  showAutocomplete = computed(() => !this.addressNotFound() && !this.hasSelectedAddress());

  addressDisplayValue = computed(() => {
    const addressControl = this.parentForm().get('address');
    const addressValue = addressControl?.value;

    if (!addressValue) {
      return '';
    }

    if (typeof addressValue === 'string') {
      return addressValue;
    }

    if (typeof addressValue === 'object' && addressValue !== null) {
      const addr = addressValue as Address;

      if (addr.formattedAddress) {
        return addr.formattedAddress;
      }

      const parts: string[] = [];

      if (addr.street) parts.push(addr.street);
      if (addr.zipCode && addr.city) {
        parts.push(`${addr.zipCode} ${addr.city}`);
      } else if (addr.city) {
        parts.push(addr.city);
      }
      if (addr.country) parts.push(addr.country);

      return (
        parts.join(', ') ||
        $localize`:@@lib.single-parent-form.address-incomplete:Adresse unvollständig`
      );
    }

    return '';
  });

  onAddressChange(address: Address): void {
    const addressControl = this.parentForm().get('address');
    const adopted = adoptGoogleMatch(address);
    addressControl?.setValue(adopted);
    addressControl?.markAsDirty();
    addressControl?.updateValueAndValidity();
    this.parentForm().markAsDirty();
  }

  onForeignAddressToggle(checked: boolean): void {
    if (checked) {
      this.addressNotFound.set(false);
    }
    this.foreignAddress.set(checked);
  }

  onSearchAgain(): void {
    this.addressNotFound.set(false);
    this.resetManualEditForm();
    const addressControl = this.parentForm().get('address');
    addressControl?.setValue(null);
    addressControl?.markAsDirty();
    addressControl?.updateValueAndValidity();
  }

  onEnterAddressManually(): void {
    this.foreignAddress.set(false);
    this.addressNotFound.set(true);
    this.resetManualEditForm();
    const addressControl = this.parentForm().get('address');
    addressControl?.setValue(null);
    addressControl?.markAsDirty();
    addressControl?.updateValueAndValidity();
  }

  private resetManualEditForm(): void {
    this.manualEditForm.reset(
      { street: '', houseNumber: '', zipCode: '', city: '' },
      { emitEvent: false },
    );
  }

  private syncManualEditFormFromAddress(address: Address | null | undefined): void {
    const edits = extractEditFields(address);
    const current = this.manualEditForm.getRawValue();
    if (
      current.street === edits.street &&
      current.houseNumber === edits.houseNumber &&
      current.zipCode === edits.zipCode &&
      current.city === edits.city
    ) {
      return;
    }
    this.manualEditForm.patchValue(edits, { emitEvent: false });
  }

  private propagateManualEdit(): void {
    const addressControl = this.parentForm().get('address');
    const currentAddress = addressControl?.value as Address | null | undefined;
    const edits = this.manualEditForm.getRawValue() as {
      street: string;
      houseNumber: string;
      zipCode: string;
      city: string;
    };

    if (this.addressNotFound()) {
      if (this.manualEditForm.invalid) {
        if (addressControl?.value !== null) {
          addressControl?.setValue(null, { emitEvent: false });
          this.addressValue.set(null);
          addressControl?.markAsDirty();
          addressControl?.updateValueAndValidity();
        }
        return;
      }
      const next = buildManualAddress(edits, new GeoPoint(0, 0));
      addressControl?.setValue(next, { emitEvent: false });
      this.addressValue.set(next);
      addressControl?.markAsDirty();
      addressControl?.updateValueAndValidity();
      this.parentForm().markAsDirty();
      return;
    }

    if (!currentAddress) {
      return;
    }

    if (!hasAddressFieldsChanged(currentAddress, edits)) {
      return;
    }

    if (this.manualEditForm.invalid) {
      addressControl?.setValue(null, { emitEvent: false });
      this.addressValue.set(null);
      addressControl?.markAsDirty();
      addressControl?.updateValueAndValidity();
      return;
    }

    const next = applyManualEdit(currentAddress, edits);
    addressControl?.setValue(next, { emitEvent: false });
    this.addressValue.set(next);
    addressControl?.markAsDirty();
    addressControl?.updateValueAndValidity();
    this.parentForm().markAsDirty();
  }

  onDecimalInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    value = value.replace(',', '.');

    if (!value || value === '' || value === '.') {
      this.parentForm()
        .get('weeklyWorkHours')
        ?.setValue(null, { emitEvent: false });
      return;
    }

    if (value.endsWith('.')) {
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      this.parentForm()
        .get('weeklyWorkHours')
        ?.setValue(numValue, { emitEvent: false });
    }
  }
}
